import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BookOpen, UserCog, Wrench, Sparkles, Clock, FileText,
  GraduationCap, Heart, MessageCircle, Lightbulb, Lock
} from 'lucide-react';
import { useEntitlements } from '../context/EntitlementsContext';
import { UpgradeGate } from '../components/UpgradeGate';

const features = [
  {
    id: 'temarios',
    icon: BookOpen,
    color: '#056A84',
    title: 'Generación de Temarios',
    description: 'Crea temarios completos para sermones, estudios bíblicos, retiros y capacitaciones. Con estructura sugerida, textos bíblicos relevantes y puntos de reflexión.',
    capabilities: [
      'Temarios para sermones dominicales',
      'Guías para estudios bíblicos grupales',
      'Programas para retiros y campamentos',
      'Currículos de capacitación ministerial',
      'Devocionales personalizados',
    ],
  },
  {
    id: 'perfil-pastoral',
    icon: UserCog,
    color: '#1F9D7A',
    title: 'Adaptación de Perfil Pastoral',
    description: 'Personaliza tu experiencia según tu estilo ministerial, denominación, tamaño de congregación y áreas de enfoque. La suite se adapta a ti.',
    capabilities: [
      'Configuración de estilo de predicación',
      'Preferencias denominacionales',
      'Áreas de enfoque ministerial',
      'Calendario litúrgico personalizado',
      'Recomendaciones basadas en tu perfil',
    ],
  },
  {
    id: 'herramientas',
    icon: Wrench,
    color: '#2B7DBA',
    title: 'Herramientas de Ayuda Pastoral',
    description: 'Recursos prácticos para el día a día del ministerio. Desde consejería hasta planificación de servicios, todo en un solo lugar.',
    capabilities: [
      'Guía de consejería pastoral',
      'Planificador de servicios y liturgia',
      'Banco de ilustraciones y recursos',
      'Protocolo para visitas hospitalarias',
      'Plantillas para ceremonias (bodas, funerales, bautismos)',
    ],
  },
];

export default function ApoyoPastoralPage() {
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const { hasModule, loading: entitlementsLoading } = useEntitlements();
  const enabled = hasModule('aliado_pastoral');
  const temariosEnabled = hasModule('temarios');

  if (!entitlementsLoading && !enabled) {
    return (
      <div>
        <TopBar title="Tu Aliado Pastoral" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo} />
        <UpgradeGate
          title="Tu Aliado Pastoral"
          description="Las herramientas de acompañamiento pastoral son un módulo adicional."
          benefits={[
            'Generación de temarios para sermones y estudios',
            'Adaptación de la Suite a tu perfil pastoral',
            'Recursos y ayuda ministerial curada',
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      <TopBar
        title="Tu Aliado Pastoral"
        onMenuClick={() => setMobileMenuOpen(true)}
        churchInfo={churchInfo}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        {/* Hero section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-[#056A84]/10 flex items-center justify-center">
              <Heart className="w-6 h-6 text-[#056A84]" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Tu Aliado Pastoral</h1>
              <p className="text-sm text-muted-foreground">Herramientas diseñadas para fortalecer tu ministerio</p>
            </div>
          </div>
          <Card className="stat-card border-[#00CCFF]/20 bg-gradient-to-r from-[#056A84]/[0.03] to-transparent">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#00CCFF]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-5 h-5 text-[#00CCFF]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Estamos preparando algo especial para ti</p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    El módulo Tu Aliado Pastoral integrará herramientas tecnológicas y recursos curados para ayudarte
                    en la preparación de sermones, consejería, planificación de servicios y mucho más.
                    Todo adaptado a tu perfil ministerial y las necesidades de tu congregación.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card key={feature.id} className="stat-card group relative overflow-hidden" data-testid={`pastoral-feature-${feature.id}`}>
                {/* Coming soon / módulo bloqueado */}
                <div className="absolute top-4 right-4">
                  {feature.id === 'temarios' && !temariosEnabled ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] font-medium bg-muted text-muted-foreground border-border flex items-center gap-1"
                      data-testid="pastoral-temarios-locked"
                    >
                      <Lock className="w-3 h-3" />
                      Mejora tu plan
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-[10px] font-medium bg-[#C58B2A]/10 text-[#C58B2A] border-[#C58B2A]/20 flex items-center gap-1"
                    >
                      <Clock className="w-3 h-3" />
                      Próximamente
                    </Badge>
                  )}
                </div>

                <CardHeader className="pb-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: `${feature.color}12` }}>
                    <Icon className="w-6 h-6" style={{ color: feature.color }} />
                  </div>
                  <CardTitle className="text-base font-semibold text-foreground pr-20">{feature.title}</CardTitle>
                </CardHeader>

                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{feature.description}</p>

                  <div className="space-y-2">
                    <p className="text-[11px] font-medium text-foreground/70 uppercase tracking-wider">Incluirá:</p>
                    <ul className="space-y-1.5">
                      {feature.capabilities.map((cap, i) => (
                        <li key={cap} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <div
                            className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                            style={{ backgroundColor: feature.color }}
                          />
                          <span>{cap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer message */}
        <div className="mt-8 text-center py-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
            <Lightbulb className="w-4 h-4" />
            <p className="text-sm font-medium">¿Tienes ideas para este módulo?</p>
          </div>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            Estamos diseñando estas herramientas pensando en las necesidades reales del ministerio.
            Tus sugerencias nos ayudan a construir algo verdaderamente útil.
          </p>
        </div>
      </div>
    </div>
  );
}
